import React, { useContext, useEffect, useState } from 'react'
import { CharacterContext } from '../../contexts/character'
import { ComparisonContext } from '../../contexts/comparison'

import Cards from '../Cards/Cards'

import './extendedInfo.scss'

const SIZES = ['Small', 'Medium', 'Large']
const ELEMENTS = [
	'Neutral',
	'Water',
	'Earth',
	'Fire',
	'Wind',
	'Poison',
	'Holy',
	'Dark',
	'Ghost',
	'Undead',
]
const RACES = [
	'Formless',
	'Undead',
	'Brute',
	'Plant',
	'Insect',
	'Fish',
	'Demon',
	'DemiHuman',
	'Angel',
	'Dragon',
]
const CLASSES = ['Normal', 'Boss']
const PLAYER_EXTRAS = [
	{ label: 'Player (Human)', key: 'RC_Player_Human' },
	{ label: 'Player (Doram)', key: 'RC_Player_Doram' },
]

// ─── Category definitions (a category = a titled block of rows) ───────────────
// Physical +% damage multipliers (melee & ranged).
const PHYS_SIZE = {
	title: 'Size',
	prefix: 'Size_',
	bonus: 'bAddSize',
	items: SIZES,
}
const PHYS_ELEMENT = {
	title: 'Element',
	prefix: 'Ele_',
	bonus: 'bAddEle',
	items: ELEMENTS,
}
const PHYS_RACE = {
	title: 'Race',
	prefix: 'RC_',
	bonus: 'bAddRace',
	items: RACES,
	extras: PLAYER_EXTRAS,
}
const PHYS_CLASS = {
	title: 'Class',
	prefix: 'Class_',
	bonus: 'bAddClass',
	items: CLASSES,
}

// Magic +% damage multipliers.
const MAG_SIZE = {
	title: 'Size',
	prefix: 'Size_',
	bonus: 'bMagicAddSize',
	items: SIZES,
}
const MAG_TARGET_ELE = {
	title: 'Target element',
	prefix: 'Ele_',
	bonus: 'bMagicAddEle',
	items: ELEMENTS,
}
const MAG_SPELL_ELE = {
	title: 'Spell element',
	prefix: 'Ele_',
	bonus: 'bMagicAtkEle',
	items: ELEMENTS,
}
const MAG_RACE = {
	title: 'Race',
	prefix: 'RC_',
	bonus: 'bMagicAddRace',
	items: RACES,
	extras: PLAYER_EXTRAS,
}
const MAG_CLASS = {
	title: 'Class',
	prefix: 'Class_',
	bonus: 'bMagicAddClass',
	items: CLASSES,
}

// Renewal variable cast time = base × (1 − √((2·DEX + INT) / 530)) × (1 − equipReduc).
// Equipment / skill reductions are stored negative (e.g. bonus bVariableCastrate,-N).
const CAST_CATEGORY = {
	title: 'Cast time',
	compute: (bonuses, stats) => {
		const round = (x) => Math.round(x * 10) / 10
		const equipVar = scalarValue(bonuses, 'bVariableCastrate') // negative = reduction
		const equipFixed = scalarValue(bonuses, 'bFixedCastrate')
		const equipReduc = -equipVar // % of variable cast removed by gear/skills
		const statFrac = Math.min(
			1,
			Math.sqrt((2 * (stats.DEX || 0) + (stats.INT || 0)) / 530),
		)
		const netRemain = (1 - statFrac) * (1 - equipReduc / 100) // fraction of base left
		const fixedSec = scalarValue(bonuses, 'bFixedCast') / 1000 // ms -> s, negative = reduction
		return [
			{ label: 'Var. equip reduc', value: round(equipReduc), unit: '%' },
			{ label: 'Var. stat reduc', value: round(statFrac * 100), unit: '%' },
			{
				label: 'Var. total reduc',
				value: round((1 - netRemain) * 100),
				unit: '%',
			},
			{ label: 'Fixed reduc', value: round(-equipFixed), unit: '%' },
			{
				label: 'Fixed flat',
				value: Math.round(fixedSec * 100) / 100,
				unit: ' s',
			},
		]
	},
}

// ─── Themes: each is a list of columns, each column a list of categories ──────
// Damage / Size / Class are short, so they share the first column; the long
// Element and Race lists get their own columns.
const THEMES = {
	melee: {
		label: 'Melee (physical)',
		columns: [
			[
				{
					title: 'Damage',
					scalars: [
						{ key: 'bShortAtkRate', label: 'Short-range dmg' },
						{ key: 'bAtkRate', label: 'ATK' },
						{ key: 'bWeaponAtkRate', label: 'Weapon ATK' },
						{ key: 'bCritAtkRate', label: 'Crit dmg' },
					],
				},
				PHYS_SIZE,
				PHYS_CLASS,
			],
			[PHYS_ELEMENT],
			[PHYS_RACE],
			[CAST_CATEGORY],
		],
	},
	ranged: {
		label: 'Ranged (physical)',
		columns: [
			[
				{
					title: 'Damage',
					scalars: [
						{ key: 'bLongAtkRate', label: 'Long-range dmg' },
						{ key: 'bAtkRate', label: 'ATK' },
						{ key: 'bWeaponAtkRate', label: 'Weapon ATK' },
						{ key: 'bCritAtkRate', label: 'Crit dmg' },
					],
				},
				PHYS_SIZE,
				PHYS_CLASS,
			],
			[PHYS_ELEMENT],
			[PHYS_RACE],
			[CAST_CATEGORY],
		],
	},
	magic: {
		label: 'Magic',
		columns: [
			[
				{
					title: 'Damage',
					scalars: [{ key: 'bMatkRate', label: 'MATK' }],
				},
				MAG_SIZE,
				MAG_CLASS,
				CAST_CATEGORY,
			],
			[MAG_TARGET_ELE],
			[MAG_SPELL_ELE],
			[MAG_RACE],
		],
	},
}

// A +% multiplier is base(prefix + item) + the catch-all (prefix + 'All').
function groupValue(bonuses, group, itemKey) {
	const b = bonuses?.[group.bonus]
	if (!b) return 0
	return (b[itemKey] || 0) + (b[group.prefix + 'All'] || 0)
}
function scalarValue(bonuses, key) {
	const v = bonuses?.[key]
	if (typeof v === 'number') return v
	if (v && typeof v === 'object' && typeof v['All'] === 'number')
		return v['All']
	return 0
}
function signed(value, unit) {
	return (value > 0 ? '+' : '') + value + unit
}

// Build the { label, value, unit } rows for a category (computed, scalars or group).
function categoryRows(bonuses, cat, stats) {
	if (cat.compute) return cat.compute(bonuses, stats)
	if (cat.scalars) {
		return cat.scalars.map((s) => ({
			label: s.label,
			value: scalarValue(bonuses, s.key),
			unit: s.flat ? '' : '%',
		}))
	}
	return [
		...cat.items.map((item) => ({
			label: item,
			value: groupValue(bonuses, cat, cat.prefix + item),
			unit: '%',
		})),
		...(cat.extras || []).map((e) => ({
			label: e.label,
			value: groupValue(bonuses, cat, e.key),
			unit: '%',
		})),
	]
}

function ExtendedInfo() {
	let { bonuses, DEX, INT } = useContext(CharacterContext)
	const { compared } = useContext(ComparisonContext)
	const [bonusesList, setBonusesList] = useState(bonuses)
	const [theme, setTheme] = useState('melee')
	useEffect(() => {
		setBonusesList(bonuses)
	}, [bonuses])

	const cfg = THEMES[theme]
	const comparing = Boolean(compared)

	// Rows for a category: current values, plus the compared build's when active.
	const buildRows = (cat) => {
		const cur = categoryRows(bonusesList, cat, { DEX, INT })
		if (!comparing) return cur.map((r) => ({ ...r }))
		const sav = categoryRows(compared.bonuses, cat, {
			DEX: compared.DEX,
			INT: compared.INT,
		})
		return cur.map((r, i) => ({
			label: r.label,
			unit: r.unit,
			value: r.value,
			saved: sav[i]?.value ?? 0,
		}))
	}

	return (
		<div className='extendedInfo'>
			<Cards Name='Bonuses'>
				<div className='themeSelect'>
					<select
						value={theme}
						onChange={(event) => setTheme(event.target.value)}
					>
						{Object.entries(THEMES).map(([key, t]) => (
							<option key={key} value={key}>
								{t.label}
							</option>
						))}
					</select>
				</div>

				<div
					className={'bonusPanel' + (comparing ? ' comparing' : '')}
				>
					{cfg.columns.map((column, i) => (
						<div className='bonusColumn' key={i}>
							{column.map((cat) => (
								<div className='bonusGroup' key={cat.title}>
									<div className='bonusGroupTitle'>
										{cat.title}
									</div>
									{buildRows(cat).map((r) => {
										const delta = comparing
											? Math.round(
													(r.value - r.saved) * 100,
												) / 100
											: 0
										return (
											<div
												className='bonusRow'
												key={r.label}
											>
												<span className='bonusLabel'>
													{r.label}
												</span>
												<span className='bonusValue'>
													{signed(r.value, r.unit)}
													{comparing && delta !== 0 && (
														<span
															className={
																'bonusDelta ' +
																(delta > 0
																	? 'up'
																	: 'down')
															}
														>
															{' ('}
															{signed(
																delta,
																r.unit,
															)}
															{')'}
														</span>
													)}
												</span>
											</div>
										)
									})}
								</div>
							))}
						</div>
					))}
				</div>
			</Cards>
		</div>
	)
}

export default ExtendedInfo
